import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback, ReactNode, Fragment } from 'react';
import { MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
export interface ColumnDef<T> {
  id: string;
  header: string;
  cell: (item: T) => ReactNode;
  className?: string;
}
export interface ActionMenuItem<T> {
  label: string;
  icon?: ReactNode;
  onClick: (item: T) => void;
  className?: string;
  /** When provided and returns false for an item, the action is hidden for that row. */
  isVisible?: (item: T) => boolean;
}
export interface PaginationConfig {
  mode: 'prev-next' | 'load-more';
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /**
   * Where to scroll after a prev/next page change.
   * - 'page': scroll to the top of the window
   * - 'table': scroll to the top of the table
   * - 'none' (default): don't scroll
   */
  scrollOnPageChange?: 'page' | 'table' | 'none';
}
interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  actions?: ActionMenuItem<T>[];
  isLoading?: boolean;
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description?: string;
    compact?: boolean;
  };
  getItemId: (item: T) => number | string;
  onSelectionChange?: (selectedIds: Set<number | string>) => void;
  selectedIds?: Set<number | string>;
  className?: string;
  bulkActions?: ReactNode;
  pagination?: PaginationConfig;
  enableSelection?: boolean;
  /** Column IDs to exclude from rendering */
  excludeColumns?: string[];
  /** Render a full-width detail row beneath a data row. Return null/undefined to skip. */
  detailRow?: (item: T) => ReactNode | null | undefined;
  /** Hide the table header row (`<thead>`). Default: false. */
  hideHeader?: boolean;
}
export function DataTable(t0) {
  const $ = _c(94);
  const {
    data,
    columns: columnsProp,
    actions,
    isLoading,
    emptyState,
    getItemId,
    onSelectionChange,
    selectedIds: externalSelectedIds,
    className,
    bulkActions,
    pagination,
    enableSelection,
    excludeColumns,
    detailRow,
    hideHeader
  } = t0;
  let t1;
  if ($[0] !== columnsProp || $[1] !== excludeColumns) {
    t1 = excludeColumns?.length ? columnsProp.filter(col => !excludeColumns.includes(col.id)) : columnsProp;
    $[0] = columnsProp;
    $[1] = excludeColumns;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const columns = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [internalSelectedIds, setInternalSelectedIds] = useState(t2);
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;
  const rootRef = useRef(null);
  let t3;
  if ($[4] !== pagination?.scrollOnPageChange) {
    t3 = handler => {
      const behavior = pagination?.scrollOnPageChange ?? "none";
      if (behavior === "page") {
        window.scrollTo({
          top: 0
        });
      } else {
        if (behavior === "table") {
          rootRef.current?.scrollIntoView({
            block: "start"
          });
        }
      }
      handler?.();
    };
    $[4] = pagination?.scrollOnPageChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  pagination?.scrollOnPageChange;
  const handlePageChange = t3;
  let t4;
  if ($[6] !== selectedIds || $[7] !== setSelectedIds) {
    t4 = itemId => {
      const newSelection = new Set(selectedIds);
      if (newSelection.has(itemId)) {
        newSelection.delete(itemId);
      } else {
        newSelection.add(itemId);
      }
      setSelectedIds(newSelection);
    };
    $[6] = selectedIds;
    $[7] = setSelectedIds;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const toggleItemSelection = t4;
  let t5;
  if ($[9] !== data || $[10] !== getItemId || $[11] !== selectedIds.size || $[12] !== setSelectedIds) {
    t5 = () => {
      if (selectedIds.size === data.length) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(data.map(getItemId)));
      }
    };
    $[9] = data;
    $[10] = getItemId;
    $[11] = selectedIds.size;
    $[12] = setSelectedIds;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toggleAllItems = t5;
  if (isLoading) {
    let t6;
    if ($[14] !== className) {
      t6 = cn("bg-white rounded-xl border border-gray-200 overflow-hidden", className);
      $[14] = className;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== actions || $[17] !== columns || $[18] !== enableSelection || $[19] !== hideHeader) {
      t7 = !hideHeader && <thead className="bg-gray-50 border-b border-gray-200"><tr>{enableSelection && <th className="px-3 py-3 text-left w-12"><div className="h-5 w-5 bg-gray-200 rounded animate-pulse" /></th>}{columns.map(_temp)}{actions && actions.length > 0 && <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}</tr></thead>;
      $[16] = actions;
      $[17] = columns;
      $[18] = enableSelection;
      $[19] = hideHeader;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = [...Array(5)];
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== actions || $[23] !== columns || $[24] !== enableSelection || $[25] !== t8) {
      t9 = <tbody className="divide-y divide-gray-200">{t8.map((_, index) => <tr key={index} className="animate-pulse">{enableSelection && <td className="px-3 py-4"><div className="h-5 w-5 bg-gray-200 rounded" /></td>}{actions && actions.length > 0 && <td className="px-2 py-1 text-right"><div className="h-8 w-8 bg-gray-200 rounded ml-auto" /></td>}{columns.map(_temp2)}</tr>)}</tbody>;
      $[22] = actions;
      $[23] = columns;
      $[24] = enableSelection;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t7 || $[28] !== t9) {
      t10 = <div className="overflow-x-auto"><table className="w-full">{t7}{t9}</table></div>;
      $[27] = t7;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t10 || $[31] !== t6) {
      t11 = <div className={t6}>{t10}</div>;
      $[30] = t10;
      $[31] = t6;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    return t11;
  }
  if (data.length === 0 && emptyState) {
    if (emptyState.compact) {
      let t6;
      if ($[33] !== emptyState.description) {
        t6 = emptyState.description && <span className="text-gray-400"> — {emptyState.description}</span>;
        $[33] = emptyState.description;
        $[34] = t6;
      } else {
        t6 = $[34];
      }
      let t7;
      if ($[35] !== emptyState.title || $[36] !== t6) {
        t7 = <p className="text-sm text-gray-500 py-3">{emptyState.title}{t6}</p>;
        $[35] = emptyState.title;
        $[36] = t6;
        $[37] = t7;
      } else {
        t7 = $[37];
      }
      return t7;
    }
    let t6;
    if ($[38] !== emptyState.icon) {
      t6 = emptyState.icon && <div className="mx-auto mb-4">{emptyState.icon}</div>;
      $[38] = emptyState.icon;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    let t7;
    if ($[40] !== emptyState.title) {
      t7 = <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyState.title}</h3>;
      $[40] = emptyState.title;
      $[41] = t7;
    } else {
      t7 = $[41];
    }
    let t8;
    if ($[42] !== emptyState.description) {
      t8 = emptyState.description && <p className="text-gray-600">{emptyState.description}</p>;
      $[42] = emptyState.description;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    let t9;
    if ($[44] !== t6 || $[45] !== t7 || $[46] !== t8) {
      t9 = <div className="text-center py-12">{t6}{t7}{t8}</div>;
      $[44] = t6;
      $[45] = t7;
      $[46] = t8;
      $[47] = t9;
    } else {
      t9 = $[47];
    }
    return t9;
  }
  let t6;
  if ($[48] !== className) {
    t6 = cn("bg-white rounded-xl border border-gray-200 overflow-hidden", className);
    $[48] = className;
    $[49] = t6;
  } else {
    t6 = $[49];
  }
  let t7;
  if ($[50] !== bulkActions || $[51] !== enableSelection || $[52] !== selectedIds.size || $[53] !== setSelectedIds) {
    t7 = enableSelection && selectedIds.size > 0 && <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between"><div className="text-sm text-gray-700">{selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""} selected</div><div className="flex items-center gap-2">{bulkActions}<Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Clear Selection</Button></div></div>;
    $[50] = bulkActions;
    $[51] = enableSelection;
    $[52] = selectedIds.size;
    $[53] = setSelectedIds;
    $[54] = t7;
  } else {
    t7 = $[54];
  }
  let t8;
  if ($[55] !== actions || $[56] !== columns || $[57] !== data.length || $[58] !== enableSelection || $[59] !== hideHeader || $[60] !== selectedIds.size || $[61] !== toggleAllItems) {
    t8 = !hideHeader && <thead className="bg-gray-50 border-b border-gray-200"><tr>{enableSelection && <th className="px-3 py-3 text-left w-12"><input type="checkbox" checked={selectedIds.size === data.length && data.length > 0} onChange={toggleAllItems} className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" /></th>}{actions && actions.length > 0 && <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" />}{columns.map(_temp3)}</tr></thead>;
    $[55] = actions;
    $[56] = columns;
    $[57] = data.length;
    $[58] = enableSelection;
    $[59] = hideHeader;
    $[60] = selectedIds.size;
    $[61] = toggleAllItems;
    $[62] = t8;
  } else {
    t8 = $[62];
  }
  let t9;
  if ($[63] !== actions || $[64] !== columns || $[65] !== data || $[66] !== detailRow || $[67] !== enableSelection || $[68] !== getItemId || $[69] !== selectedIds || $[70] !== toggleItemSelection) {
    let t10;
    if ($[72] !== actions || $[73] !== columns || $[74] !== detailRow || $[75] !== enableSelection || $[76] !== getItemId || $[77] !== selectedIds || $[78] !== toggleItemSelection) {
      t10 = item => {
        const itemId_0 = getItemId(item);
        const detailContent = detailRow?.(item);
        const totalColSpan = columns.length + (enableSelection ? 1 : 0) + (actions && actions.length > 0 ? 1 : 0);
        return <Fragment key={itemId_0}><tr className={cn("hover:bg-gray-50 transition-colors", selectedIds.has(itemId_0) && "bg-primary-50")}>{enableSelection && <td className="px-3 py-4 cursor-pointer" onClick={() => toggleItemSelection(itemId_0)}><input type="checkbox" checked={selectedIds.has(itemId_0)} onChange={() => toggleItemSelection(itemId_0)} onClick={_temp4} className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" /></td>}{actions && actions.length > 0 && <td className="px-2 py-1 text-right"><DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="start">{actions.filter(action => action.isVisible === undefined || action.isVisible(item)).map((action_0, index_0) => <DropdownMenuItem key={index_0} onClick={() => action_0.onClick(item)} className={cn("cursor-pointer", action_0.className)}>{action_0.icon && <span className="mr-2">{action_0.icon}</span>}{action_0.label}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu></td>}{columns.map(column_2 => <td key={column_2.id} className={cn("px-6 py-2.5", column_2.className)}>{column_2.cell(item)}</td>)}</tr>{detailContent && <tr className="bg-gray-50/50"><td colSpan={totalColSpan} className="px-6 py-2">{detailContent}</td></tr>}</Fragment>;
      };
      $[72] = actions;
      $[73] = columns;
      $[74] = detailRow;
      $[75] = enableSelection;
      $[76] = getItemId;
      $[77] = selectedIds;
      $[78] = toggleItemSelection;
      $[79] = t10;
    } else {
      t10 = $[79];
    }
    t9 = data.map(t10);
    $[63] = actions;
    $[64] = columns;
    $[65] = data;
    $[66] = detailRow;
    $[67] = enableSelection;
    $[68] = getItemId;
    $[69] = selectedIds;
    $[70] = toggleItemSelection;
    $[71] = t9;
  } else {
    t9 = $[71];
  }
  let t10;
  if ($[80] !== t9) {
    t10 = <tbody className="divide-y divide-gray-200">{t9}</tbody>;
    $[80] = t9;
    $[81] = t10;
  } else {
    t10 = $[81];
  }
  let t11;
  if ($[82] !== t10 || $[83] !== t8) {
    t11 = <div className="overflow-x-auto"><table className="w-full">{t8}{t10}</table></div>;
    $[82] = t10;
    $[83] = t8;
    $[84] = t11;
  } else {
    t11 = $[84];
  }
  let t12;
  if ($[85] !== handlePageChange || $[86] !== isLoading || $[87] !== pagination) {
    t12 = pagination && <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">{pagination.mode === "prev-next" ? <><Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.onPrevPage)} disabled={!pagination.hasPrevPage || isLoading} className="gap-2"><ChevronLeft className="h-4 w-4" />Previous</Button><Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.onNextPage)} disabled={!pagination.hasNextPage || isLoading} className="gap-2">Next<ChevronRight className="h-4 w-4" /></Button></> : <div className="w-full flex justify-center"><Button variant="outline" size="sm" onClick={pagination.onLoadMore} disabled={!pagination.hasNextPage || pagination.isLoadingMore} className="gap-2">{pagination.isLoadingMore ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />Loading...</> : "Load More"}</Button></div>}</div>;
    $[85] = handlePageChange;
    $[86] = isLoading;
    $[87] = pagination;
    $[88] = t12;
  } else {
    t12 = $[88];
  }
  let t13;
  if ($[89] !== t11 || $[90] !== t12 || $[91] !== t6 || $[92] !== t7) {
    t13 = <div ref={rootRef} className={t6}>{t7}{t11}{t12}</div>;
    $[89] = t11;
    $[90] = t12;
    $[91] = t6;
    $[92] = t7;
    $[93] = t13;
  } else {
    t13 = $[93];
  }
  return t13;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(column_1) {
  return <th key={column_1.id} className={cn("px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", column_1.className)}>{column_1.header}</th>;
}
function _temp2(column_0) {
  return <td key={column_0.id} className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-3/4" /></td>;
}
function _temp(column) {
  return <th key={column.id} className={cn("px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", column.className)}>{column.header}</th>;
}